<template>
    <div class="shopping-cart-menu">
        <QitBtn
            padding="sm"
            :flat="!isXSmall"
            :icon="isXSmall ? mobileIcon : icon"
            size="13px"
            class="grey-hover"
            color="default"
            data-qs="open-shopping-cart-button"
            @click="redirectToBasket"
            :disabled="!store.isShopOnline"
        >
            <q-badge v-if="basketItemCount > 0" color="negative" floating style="z-index: 9999">
                {{ basketItemCount }}
            </q-badge>
            <q-tooltip self="top right">{{ $t(shoppingCartTooltip) }}</q-tooltip>
        </QitBtn>
    </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import QitBtn from "@/shared/components/buttons/qit-button.vue";
import { useScreenSize } from "@/shared/screen/composables/screen-size";
import { useDocumentVisibility } from "@vueuse/core";
import { computed, onMounted, onUnmounted, ref, watch, watchEffect } from "vue";
import { useShopLight } from "../../../shop-light/composable/use-shop-light";
import { useShopConnector } from "../../composables/shop-connector";
import { useShopEvents } from "../../composables/use-shop-events";
import { useShopStatusStore } from "../../store/shop-status.store";

withDefaults(
    defineProps<{
        icon?: string;
        mobileIcon?: string;
    }>(),
    {
        icon: "fa-regular fa-cart-shopping icon-color",
        mobileIcon: "fa-regular fa-cart-shopping text-on-primary-light-color",
    }
);

const { isXSmall } = useScreenSize();
const visibility = useDocumentVisibility();

// Shop state
const { redirectToBasket, getBasket } = useShopConnector();
const store = useShopStatusStore();
const { getBasketCount } = useShopLight();

// Basket data state
const basket = ref<any>(undefined);
const basketItemCount = ref(0);
const pollingEnabled = ref(visibility.value === "visible");
let pollingInterval: number | null = null;

// Setup polling function
async function updateBasketData() {
    try {
        const data = await getBasket();
        if (data) {
            basket.value = data;
            // Update item count
            let count = 0;

            if (store.isShopLight) {
                count = await getBasketCount();
            } else {
                data.basket?.items?.forEach((item: any) => {
                    count += item.amount;
                });
            }

            basketItemCount.value = count;
        }
    } catch (error) {
        console.error("[ShoppingCart] Error fetching basket:", error);
    }
}

// Watch visibility changes to control polling
watch(visibility, async (isVisible) => {
    pollingEnabled.value = isVisible === "visible";

    if (pollingEnabled.value) {
        // Immediate update when becoming visible
        await updateBasketData();
        // Start polling
        if (!pollingInterval) {
            pollingInterval = window.setInterval(updateBasketData, 10000);
        }
    } else if (pollingInterval) {
        // Stop polling when tab is hidden
        window.clearInterval(pollingInterval);
        pollingInterval = null;
    }
});

// Get shop events
const { basketUpdateTrigger } = useShopEvents();

// Watch for basket update events
watchEffect(() => {
    // Watch the basket update trigger
    // TODO: fix
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    basketUpdateTrigger.value;
    if (pollingEnabled.value) {
        updateBasketData();
    }
});

// Setup initial polling
onMounted(async () => {
    if (pollingEnabled.value) {
        await updateBasketData();
        pollingInterval = window.setInterval(updateBasketData, 10000);
    }
});

// Cleanup on unmount
onUnmounted(() => {
    if (pollingInterval) {
        window.clearInterval(pollingInterval);
        pollingInterval = null;
    }
});

const shoppingCartTooltip = computed(() => {
    if (store.isShopOnline) {
        return "shared.Shopping cart";
    } else {
        return "shop.The store is currently unavailable due to maintenance work or errors";
    }
});
</script>

<style lang="scss" scoped>
.shopping-cart-menu {
    &:deep(.q-btn:disabled) {
        background: transparent !important;
    }

    &:deep(.q-btn--flat:not([disabled]):hover .q-btn__content) {
        text-decoration: none;
    }
}
</style>
